import {
	Button,
	Checkbox,
	FormControlLabel,
	MuiThemeProvider,
	createMuiTheme,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	deleteVisitType,
	getCalendlyVisits,
	getVisitTypeById,
	postVisitType,
	updateVisitType,
} from "../../../../api/visitType";
import { checkIsEmpty } from "../../../../utils/helpers";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { getStripeVisits } from "../../../../api/purchases";
import MyAutocomplete from "../../../components/MyAutocomplete";
import { changes, setChanges } from "../../../../utils/globals";

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptyVisitType() {
	return {
		fullName: {},
		idStripe: "",
		active: true,
	};
}

export default function EditVisitTypesPage() {
	const [visitType, setVisitType] = useState(getEmptyVisitType());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(0);
	const [stripeIds, setStripeIds] = useState([]);
	const [calendlyVisitTypes, setCalendlyVisitTypes] = useState([]);

	const visitTypeId = useParams().id;
	const history = useHistory();
	const user = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	// const [changes, setChanges] = useState(-2);
	useEffect(() => setChanges(-2), []);
	useEffect(() => {
		if (!visitTypeId) setChanges(changes+2);
		else setChanges(changes+1);
	}, [visitType])

	useEffect(() => {
		if (!visitTypeId) {
			disableLoadingData();
			return;
		}
		getVisitTypeById(visitTypeId)
			.then((res) => {
				if (res.status === 200) {
					setVisitType(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "Could not get visit type.",
				});
				history.push("/visit-types");
			});
	}, [visitTypeId, disableLoadingData, history]);

	useEffect(() => {
		getStripeVisits()
			.then((res) => {
				setStripeIds(res.data);
			})
			.catch((error) => {
				alertError({ error, customMessage: "Could not find stripeIds."});
			})
		getCalendlyVisits()
			.then((res) => {
				setCalendlyVisitTypes(res.data);
			})
			.catch((error) => {
				alertError({ error, customMessage: "Could not find visit types of calendly."});
			})
	}, [])

	function saveVisitType() {
		if (checkIsEmpty(visitType.fullName))
			return alertError({
				error: null,
				customMessage:
					"The name is required in at least one of the languages.",
			});
		else if (!visitType?.idStripe) 
			return alertError({ error: null, customMessage: "The id stripe is required." });
		if (!visitTypeId) {
			postVisitType(visitType, null)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: "Saved!",
							customMessage: "Visit type successfully created.",
						});
						history.push("/visit-types");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save visit type.",
					});
				});
		} else {
			updateVisitType(visitTypeId, visitType, null)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: "Saved!",
							customMessage: "Changes successfully saved.",
						});
						history.push("/visit-types");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save changes.",
					});
				});
		}
	}

	const handleChange = (element, lang) => (event) => {
		if (event.target.value === " ") return;
		if (lang) {
			if (!visitType[element]) visitType[element] = {};
			let newText = visitType[element];
			newText[lang] = event.target.value;
			setVisitType({ ...visitType, [element]: newText });
		} else
			setVisitType({
				...visitType,
				[element]: event.target.value,
			});
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
				<CardHeader title={visitType?._id?'Visit type: ' + (visitType.fullName?visitType.fullName.es:''):'New visit type'}>
					<div className="mt-5">
						<Button
							onClick={() => {
								if (changes > 0) setOpenConfirmDialog(1);
								else history.push("/visit-types");
							}}
							variant="outlined"
							style={{ marginRight: "20px" }}
						>
							Back
						</Button>
						<Button
							onClick={() => saveVisitType()}
							variant="outlined"
							color="primary"
							style={{ marginRight: "20px" }}
						>
							Save visit type
						</Button>
						<ConfirmDialog
							title={
								"Are you sure you want to go back? You will lose all your changes"
							}
							open={openConfirmDialog === 1}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								setChanges(0);
								history.push("/visit-types");
							}}
						/>
						{visitTypeId && user?.role.includes("admin") && (
							<>
								<MuiThemeProvider theme={theme}>
									<Button
										onClick={() => setOpenConfirmDialog(2)}
										variant="outlined"
										color="secondary"
										style={{ marginRight: "20px" }}
									>
										Delete visit type
									</Button>
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											marginLeft: "auto",
										}}
									></div>
								</MuiThemeProvider>

								<ConfirmDialog
									title={
										"Are you sure you want to delete this visit type?"
									}
									open={openConfirmDialog === 2}
									setOpen={setOpenConfirmDialog}
									onConfirm={() => {
										deleteVisitType(visitTypeId)
											.then((res) => {
												if (
													res.status === 204 ||
													res.status === 200
												) {
													alertSuccess({
														title: "Deleted!",
														customMessage:
															"Visit type deleted successfully",
													});
													setChanges(0)
													history.push("/visit-types");
												}
											})
											.catch((error) => {
												alertError({
													error: error,
													customMessage:
														"Could not delete visit type.",
												});
											});
									}}
								/>
							</>
						)}
					</div>
				</CardHeader>
					<CardBody>
						{/* <h4 style={{color: 'red'}}>REMEMBER THAT THE NAME NEEDS TO BE EQUAL TO THE CALENDLY VISIT NAME</h4> */}
						<br />
						{/* <MultilanguageTabBlock
							multilanguageTabContent={
								renderMultilanguageTabContent
							}
						/> */}
						<MyAutocomplete
							options={ calendlyVisitTypes }
							getOptionLabel={(option) => option}
							value={visitType?.fullName?.es}
							onChange={(event, selected) => {
								setVisitType({...visitType, fullName: { es: selected }});
							}}
							placeholder="Select the visit type"
							label={"Full name"}
						/>
						<MyAutocomplete
							options={ stripeIds }
							getOptionLabel={(option) => option}
							value={visitType?.idStripe}
							onChange={(event, selected) => {
								setVisitType({...visitType, idStripe: selected});
							}}
							placeholder="Select the id stripe of the visit"
							label={"Id Stripe (is the id of the visit product in Stripe)"}
						/>
						{/* <TextField
							id={`isStripe`}
							label="Id Stripe (is the id of the visit product in Stripe)"
							value={visitType?.idStripe}
							onChange={handleChange("idStripe", null)}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="standard"
							required
						/> */}
						<FormControlLabel
							control={
								<Checkbox
									checked={visitType.active}
									onChange={() =>
										setVisitType({
											...visitType,
											active: !visitType.active,
										})
									}
									name="checkActive"
								/>
							}
							label="Active"
						/>
					</CardBody>
				</Card>
			</>
		);
}
